import type { FC, ReactNode } from 'react'
import type { KarakaRole, KarakaSentence } from '@/sanskrit/lib/content/karaka'

import Link from 'next/link'
import { Fragment, useMemo, useState } from 'react'
import { motion } from 'motion/react'
import { ArrowRight, Check, HelpCircle, RotateCcw, X } from 'lucide-react'
import { cn } from '@/sanskrit/lib/utils'
import { Seo } from '@/sanskrit/components/generals/seo'
import { Layout } from '@/sanskrit/components/generals/layout'
import { SanskritGate } from '@/components/plans/sanskrit-gate'
import { Button } from '@/components/ui/button'
import { PageHeader, Segmented } from '@/sanskrit/components/vani/bits'
import { useAppState, useT } from '@/sanskrit/components/providers/app-state'
import { pickAnalysis } from '@/sanskrit/lib/sanskrit/analyzer'
import { getDhatu } from '@/sanskrit/lib/sanskrit/dhatus'
import { GENDER_LABEL, LAKARA_LABEL, PURUSHA_LABEL, VACANA_LABEL, VIBHAKTI_LABEL } from '@/sanskrit/lib/sanskrit/labels'
import { addXp, recordAnswer } from '@/sanskrit/lib/progress/engine'
import { KARAKA_ROLES, KARAKA_SENTENCES } from '@/sanskrit/lib/content/karaka'
import AudioButton from '@/sanskrit/components/vani/audio-button'
import Skt from '@/sanskrit/components/vani/skt'

const ROLE_STYLE: Record<KarakaRole, string> = {
    kriya: 'border-ink bg-ink text-primary-foreground',
    karta: 'border-saffron bg-saffron-soft text-ink',
    karma: 'border-terracotta bg-terracotta-soft text-ink',
    karana: 'border-ink/60 bg-ink-soft text-ink',
    sampradana: 'border-tulsi bg-tulsi-soft text-ink',
    apadana: 'border-saffron-strong bg-card text-ink',
    sambandha: 'border-dashed border-muted-foreground/60 bg-muted text-ink',
    adhikarana: 'border-tulsi/70 bg-card text-ink',
    sambodhana: 'border-dashed border-terracotta bg-card text-ink',
    visesana: 'border-dashed border-saffron bg-card text-ink',
    avyaya: 'border-dashed border-border bg-card text-muted-foreground'
}

const ROLE_DOT: Record<KarakaRole, string> = {
    kriya: 'bg-ink',
    karta: 'bg-saffron',
    karma: 'bg-terracotta',
    karana: 'bg-ink/60',
    sampradana: 'bg-tulsi',
    apadana: 'bg-saffron-strong',
    sambandha: 'bg-muted-foreground/60',
    adhikarana: 'bg-tulsi/70',
    sambodhana: 'bg-terracotta/60',
    visesana: 'bg-saffron/60',
    avyaya: 'bg-border'
}

const LEGEND: KarakaRole[] = ['kriya', 'karta', 'karma', 'karana', 'sampradana', 'apadana', 'adhikarana', 'sambandha', 'sambodhana', 'visesana']

function WordFacts({ text, hint }: { text: string; hint?: string }) {
    const t = useT()
    const { primary } = pickAnalysis(text, hint)
    if (!primary) return null

    if (primary.kind === 'verb') {
        const d = getDhatu(primary.dhatuId)

        return (
            <p className='text-sm'>
                <span lang='sa' className='deva font-semibold'>{d?.root}</span>
                {' · '}<span lang='sa' className='deva'>{LAKARA_LABEL[primary.lakara].sa}</span>
                {' · '}{t({ hi: PURUSHA_LABEL[primary.purusha].hi, en: PURUSHA_LABEL[primary.purusha].en })}
                {' · '}{t({ hi: VACANA_LABEL[primary.vacana].hi, en: VACANA_LABEL[primary.vacana].en })}
            </p>
        )
    }

    if (primary.kind === 'noun' || primary.kind === 'pronoun' || primary.kind === 'adjective') {
        return (
            <p className='text-sm'>
                <span lang='sa' className='deva font-semibold'>{primary.stem}</span>
                {' · '}{t({ hi: GENDER_LABEL[primary.gender].hi, en: GENDER_LABEL[primary.gender].en })}
                {' · '}<span lang='sa' className='deva'>{VIBHAKTI_LABEL[primary.vibhakti].sa}</span>
                {' · '}{t({ hi: VACANA_LABEL[primary.vacana].hi, en: VACANA_LABEL[primary.vacana].en })}
            </p>
        )
    }

    return <p className='text-sm text-muted-foreground'>{t(primary.meaning)}</p>
}

function Diagram({ sentence, selected, onSelect, focusRole }: { sentence: KarakaSentence; selected: number | null; onSelect: (i: number) => void; focusRole: KarakaRole | null }) {
    const t = useT()
    const verbIndex = sentence.words.findIndex(w => w.role === 'kriya')
    const verb = sentence.words[verbIndex]
    const dependents = sentence.words.map((w, i) => ({ w, i })).filter(({ w, i }) => i !== verbIndex && w.head === undefined)
    const childrenOf = (index: number) => sentence.words.map((w, i) => ({ w, i })).filter(({ w }) => w.head === index)
    const n = Math.max(dependents.length, 1)

    const node = (i: number, compact = false) => {
        const w = sentence.words[i]
        const role = KARAKA_ROLES[w.role]
        const dimmed = focusRole !== null && focusRole !== w.role

        return (
            <button
                key={i}
                type='button'
                onClick={() => onSelect(i)}
                aria-pressed={selected === i}
                className={cn(
                    'flex w-full flex-col items-center rounded-2xl border-2 px-2 transition-all',
                    compact ? 'py-1.5' : 'py-3',
                    ROLE_STYLE[w.role],
                    selected === i && 'ring-4 ring-saffron/40',
                    dimmed && 'opacity-35'
                )}
            >
                <span lang='sa' className={cn('deva font-semibold', compact ? 'text-lg' : 'text-2xl')}>{w.text}</span>
                <span lang='sa' className='deva text-xs font-semibold opacity-80'>{role.sa}</span>
                {role.vibhakti && !compact && <span lang='sa' className='deva text-[0.68rem] opacity-70'>{VIBHAKTI_LABEL[role.vibhakti].sa}</span>}
                {w.role === 'kriya' && <span className='text-[0.68rem] opacity-75'>{t({ hi: 'केन्द्र', en: 'centre' })}</span>}
            </button>
        )
    }

    return (
        <div className='no-scrollbar-x overflow-x-auto pb-2'>
            <div className='mx-auto' style={{ minWidth: `${Math.max(n * 128, 260)}px` }}>
                <div className='mx-auto w-40'>{verbIndex >= 0 && verb && node(verbIndex)}</div>
                <svg viewBox='0 0 100 30' preserveAspectRatio='none' className='h-12 w-full' aria-hidden='true'>
                    {dependents.map(({ w, i }, k) => (
                        <motion.path
                            key={`${sentence.id}-${i}`}
                            d={`M50 0 L${((k + 0.5) * 100) / n} 30`}
                            fill='none'
                            stroke='currentColor'
                            strokeWidth={2}
                            strokeDasharray={w.role === 'sambodhana' ? '4 4' : undefined}
                            vectorEffect='non-scaling-stroke'
                            className={cn('text-ink/40', focusRole === w.role && 'text-saffron-strong')}
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{ duration: 0.5, delay: 0.1 * k }}
                        />
                    ))}
                </svg>
                <div className='grid gap-3' style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}>
                    {dependents.map(({ i }) => (
                        <div key={i} className='flex flex-col items-center'>
                            {node(i)}
                            {childrenOf(i).map(child => (
                                <Fragment key={child.i}>
                                    <span className='h-5 w-px border-l-2 border-dashed border-ink/30' aria-hidden='true' />
                                    {node(child.i, true)}
                                    {childrenOf(child.i).map(grand => (
                                        <Fragment key={grand.i}>
                                            <span className='h-4 w-px border-l-2 border-dashed border-ink/30' aria-hidden='true' />
                                            {node(grand.i, true)}
                                        </Fragment>
                                    ))}
                                </Fragment>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

interface QuizState {
    order: number[]
    pos: number
    score: number
    missed: boolean
    wrongPick: number | null
    found: number[]
}

const KarakaPage: FC = (): ReactNode => {
    const t = useT()
    const { settings, updateProgress } = useAppState()
    const [sentenceId, setSentenceId] = useState(KARAKA_SENTENCES[0].id)
    const [selected, setSelected] = useState<number | null>(null)
    const [focusRole, setFocusRole] = useState<KarakaRole | null>(null)
    const [mode, setMode] = useState<'explore' | 'quiz'>('explore')
    const [quiz, setQuiz] = useState<QuizState | null>(null)
    const sentence = KARAKA_SENTENCES.find(s => s.id === sentenceId) ?? KARAKA_SENTENCES[0]
    const sentenceIndex = KARAKA_SENTENCES.indexOf(sentence)
    const askable = useMemo(() => sentence.words.map((w, i) => ({ w, i })).filter(({ w }) => w.ask).map(({ i }) => i), [sentence])
    const selectedWord = selected !== null ? sentence.words[selected] : null

    const startQuiz = () => {
        setSelected(null)
        setFocusRole(null)
        setQuiz({ order: askable, pos: 0, score: 0, missed: false, wrongPick: null, found: [] })
    }

    const pickSentence = (id: string) => {
        setSentenceId(id)
        setSelected(null)
        setFocusRole(null)
        setQuiz(null)
    }

    const switchMode = (m: 'explore' | 'quiz') => {
        setMode(m)
        if (m === 'quiz') {
            const next = KARAKA_SENTENCES.find(s => s.id === sentenceId) ?? KARAKA_SENTENCES[0]
            const order = next.words.map((w, i) => ({ w, i })).filter(({ w }) => w.ask).map(({ i }) => i)
            setQuiz({ order, pos: 0, score: 0, missed: false, wrongPick: null, found: [] })
        }
        else {
            setQuiz(null)
        }
    }

    const answer = (index: number) => {
        if (!quiz || quiz.pos >= quiz.order.length) return
        const target = quiz.order[quiz.pos]
        if (quiz.found.includes(index)) return
        const targetWord = sentence.words[target]
        const vib = KARAKA_ROLES[targetWord.role].vibhakti ?? 0
        const ok = index === target

        if (!quiz.missed) {
            const pickedVib = KARAKA_ROLES[sentence.words[index].role].vibhakti
            updateProgress(p => recordAnswer(p, {
                item: `vibhakti:karaka-${sentence.id}-${target}:${vib}`,
                skill: 'vibhakti',
                ok,
                confusions: !ok && pickedVib && vib ? [`vibhakti:${vib}>${pickedVib}`] : undefined
            }))
        }

        if (ok) {
            const score = quiz.score + (quiz.missed ? 0 : 1)
            const nextPos = quiz.pos + 1
            if (nextPos >= quiz.order.length) updateProgress(p => addXp(p, score * 2))
            setQuiz({ ...quiz, pos: nextPos, score, missed: false, wrongPick: null, found: [...quiz.found, index] })
        }
        else {
            setQuiz({ ...quiz, missed: true, wrongPick: index })
        }
    }

    const current = quiz && quiz.pos < quiz.order.length ? sentence.words[quiz.order[quiz.pos]] : null
    const finished = quiz !== null && quiz.pos >= quiz.order.length

    return (
        <Layout>
            <Seo title='Kāraka' description='Sanskrit sentence diagrams: see how every word relates to the action as agent, object, instrument, recipient, source or location, then test yourself.' />

            <SanskritGate what='The kāraka page'>
            <PageHeader
                sa='कारकम्'
                title={{ hi: 'कारक: वाक्य का चित्र', en: 'Kāraka: the picture of a sentence' }}
                subtitle={{
                    hi: 'हर संस्कृत वाक्य के केन्द्र में क्रिया है। बाकी शब्द बताते हैं कि क्रिया को कौन करता है, किस पर, किससे, किसके लिए, कहाँ से और कहाँ।',
                    en: 'Every Sanskrit sentence has the action at its centre. The other words tell who does it, to what, with what, for whom, from where and where.'
                }}
            />

            <div className='mx-auto max-w-6xl space-y-6 px-4 pb-16 sm:px-6'>
                <div className='flex flex-wrap items-center justify-between gap-3'>
                    <Segmented
                        label={t({ hi: 'मोड', en: 'Mode' })}
                        value={mode}
                        onChange={switchMode}
                        options={[
                            { value: 'explore', label: t({ hi: 'देखिए और समझिए', en: 'Explore' }) },
                            { value: 'quiz', label: t({ hi: 'प्रश्न पूछिए', en: 'Question game' }) }
                        ]}
                    />
                    <div className='flex flex-wrap gap-1.5'>
                        {KARAKA_SENTENCES.map((s, i) => (
                            <button key={s.id} type='button' onClick={() => pickSentence(s.id)} aria-pressed={s.id === sentence.id} className={cn('size-9 rounded-full border text-sm font-semibold transition-colors', s.id === sentence.id ? 'border-ink bg-ink text-primary-foreground' : 'border-border bg-card hover:border-saffron')}>
                                {i + 1}
                            </button>
                        ))}
                    </div>
                </div>

                <section className='rounded-[2rem] border border-border bg-card p-5 shadow-sm sm:p-8'>
                    <div className='flex flex-wrap items-start justify-between gap-3'>
                        <div>
                            {mode === 'quiz'
                                ? <p lang='sa' className='deva text-3xl font-semibold text-ink sm:text-4xl'>{sentence.sa}</p>
                                : <Skt text={sentence.sa} hints={Object.fromEntries(sentence.words.filter(w => w.hint).map(w => [w.text, w.hint as string]))} iast='auto' className='text-3xl font-semibold text-ink sm:text-4xl' />}
                            <p className='mt-1 text-muted-foreground'>{t({ hi: sentence.hi, en: sentence.en })}</p>
                        </div>
                        <AudioButton text={sentence.sa} />
                    </div>

                    {mode === 'explore' && (
                        <>
                            <div className='mt-6'>
                                <Diagram sentence={sentence} selected={selected} onSelect={setSelected} focusRole={focusRole} />
                            </div>

                            <div className='mt-5 flex flex-wrap gap-2' role='group' aria-label={t({ hi: 'भूमिकाएँ', en: 'Roles' })}>
                                {LEGEND.map(r => (
                                    <button
                                        key={r}
                                        type='button'
                                        onClick={() => setFocusRole(focusRole === r ? null : r)}
                                        aria-pressed={focusRole === r}
                                        className={cn('inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors', focusRole === r ? 'border-ink bg-ink text-primary-foreground' : 'border-border bg-background hover:border-saffron', !sentence.words.some(w => w.role === r) && 'opacity-50')}
                                    >
                                        <span className={cn('size-2.5 rounded-full', ROLE_DOT[r])} aria-hidden='true' />
                                        <span lang='sa' className='deva'>{KARAKA_ROLES[r].sa}</span>
                                    </button>
                                ))}
                            </div>

                            {selectedWord
                                ? (
                                    <motion.div key={`${sentence.id}-${selected}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className='mt-5 rounded-2xl bg-saffron-soft/70 p-5'>
                                        <div className='flex flex-wrap items-baseline gap-x-3 gap-y-1'>
                                            <Skt text={selectedWord.text} hints={selectedWord.hint ? { [selectedWord.text]: selectedWord.hint } : undefined} className='text-3xl font-semibold text-ink' />
                                            <span lang='sa' className='deva text-lg font-semibold text-saffron-strong'>{KARAKA_ROLES[selectedWord.role].sa}</span>
                                            <span className='text-sm text-muted-foreground'>{t(KARAKA_ROLES[selectedWord.role].name)}</span>
                                        </div>
                                        <WordFacts text={selectedWord.text} hint={selectedWord.hint} />
                                        <p className='mt-2'>{t(KARAKA_ROLES[selectedWord.role].desc)}</p>
                                        {selectedWord.ask && (
                                            <p className='mt-2 text-sm'>
                                                <HelpCircle className='mr-1 inline size-4 text-saffron-strong' />
                                                {selectedWord.ask.sa && <span lang='sa' className='deva mr-2 font-semibold'>{selectedWord.ask.sa}</span>}
                                                <span className='text-muted-foreground'>{t({ hi: selectedWord.ask.hi, en: selectedWord.ask.en })}</span>
                                            </p>
                                        )}
                                        {settings.mode === 'adult' && KARAKA_ROLES[selectedWord.role].sutra && (
                                            <p className='mt-2 text-xs text-muted-foreground'>
                                                {t({ hi: 'सूत्र:', en: 'Sūtra:' })} <span lang='sa' className='deva'>{KARAKA_ROLES[selectedWord.role].sutra}</span>
                                            </p>
                                        )}
                                    </motion.div>
                                )
                                : (
                                    <p className='mt-5 rounded-2xl border border-dashed border-border px-4 py-3 text-sm text-muted-foreground'>
                                        {t({ hi: 'आरेख के किसी भी खाने को छुइए: उसकी भूमिका, विभक्ति और प्रश्न यहाँ दिखेंगे। ऊपर के वाक्य का कोई भी शब्द छूने पर उसका पूरा विवरण खुलता है।', en: 'Tap any box in the diagram to see its role, case and question. Tap a word in the sentence above for its full details.' })}
                                    </p>
                                )}

                            {sentence.note && <p className='mt-4 rounded-2xl bg-terracotta-soft/60 px-4 py-3 text-sm'>{t(sentence.note)}</p>}
                        </>
                    )}

                    {mode === 'quiz' && quiz && (
                        <div className='mt-6'>
                            {current && (
                                <div className='rounded-2xl bg-saffron-soft/70 p-4'>
                                    <p className='text-xs font-semibold text-muted-foreground'>
                                        {t({ hi: `प्रश्न ${quiz.pos + 1} / ${quiz.order.length}`, en: `Question ${quiz.pos + 1} of ${quiz.order.length}` })}
                                    </p>
                                    {current.ask?.sa && <p lang='sa' className='deva mt-1 text-2xl font-semibold text-ink'>{current.ask.sa}</p>}
                                    <p className={cn(current.ask?.sa ? 'text-muted-foreground' : 'mt-1 text-lg font-semibold')}>{current.ask ? t({ hi: current.ask.hi, en: current.ask.en }) : ''}</p>
                                    <p className='mt-1 text-sm'>{t({ hi: 'उत्तर वाला शब्द छुइए।', en: 'Tap the word that answers it.' })}</p>
                                </div>
                            )}

                            <div className='mt-4 flex flex-wrap gap-2'>
                                {sentence.words.map((w, i) => {
                                    const found = quiz.found.includes(i)
                                    const wrong = quiz.wrongPick === i

                                    return (
                                        <motion.button
                                            key={i}
                                            type='button'
                                            onClick={() => answer(i)}
                                            disabled={finished}
                                            animate={wrong ? { x: [0, -6, 6, -4, 4, 0] } : { x: 0 }}
                                            transition={{ duration: 0.35 }}
                                            className={cn(
                                                'min-h-14 rounded-2xl border-2 px-4 py-2 text-left transition-colors',
                                                found ? ROLE_STYLE[w.role] : wrong ? 'border-destructive bg-destructive/10' : 'border-border bg-background hover:border-saffron'
                                            )}
                                        >
                                            <span lang='sa' className='deva block text-2xl font-semibold'>{w.text}</span>
                                            {found && <span lang='sa' className='deva block text-xs font-semibold'>{KARAKA_ROLES[w.role].sa}</span>}
                                        </motion.button>
                                    )
                                })}
                            </div>

                            {quiz.wrongPick !== null && current && (
                                <p className='mt-3 flex items-start gap-2 text-sm text-terracotta'>
                                    <X className='mt-0.5 size-4 shrink-0' />
                                    {t({
                                        hi: `यह नहीं। संकेत: उत्तर ${KARAKA_ROLES[current.role].sa} है${KARAKA_ROLES[current.role].vibhakti ? `, जो ${VIBHAKTI_LABEL[KARAKA_ROLES[current.role].vibhakti!].sa} विभक्ति में होता है` : ''}।`,
                                        en: `Not that one. Hint: the answer is the ${KARAKA_ROLES[current.role].name.en.toLowerCase()} (${KARAKA_ROLES[current.role].sa})${KARAKA_ROLES[current.role].vibhakti ? `, which stands in ${VIBHAKTI_LABEL[KARAKA_ROLES[current.role].vibhakti!].sa}` : ''}.`
                                    })}
                                </p>
                            )}

                            {finished && (
                                <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className='mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-tulsi-soft px-5 py-4'>
                                    <p className='flex items-center gap-2 font-semibold text-tulsi'>
                                        <Check className='size-5' />
                                        {t({ hi: `पहली बार में सही: ${quiz.score} / ${quiz.order.length}`, en: `Right first time: ${quiz.score} of ${quiz.order.length}` })}
                                    </p>
                                    <div className='flex gap-2'>
                                        <Button variant='outline' onClick={startQuiz}>
                                            <RotateCcw className='size-4' />
                                            {t({ hi: 'फिर से', en: 'Again' })}
                                        </Button>
                                        <Button onClick={() => {
                                            const next = KARAKA_SENTENCES[(sentenceIndex + 1) % KARAKA_SENTENCES.length]
                                            setSentenceId(next.id)
                                            setQuiz({ order: next.words.map((w, i) => ({ w, i })).filter(({ w }) => w.ask).map(({ i }) => i), pos: 0, score: 0, missed: false, wrongPick: null, found: [] })
                                        }}>
                                            {t({ hi: 'अगला वाक्य', en: 'Next sentence' })}
                                            <ArrowRight className='size-4' />
                                        </Button>
                                    </div>
                                </motion.div>
                            )}
                        </div>
                    )}
                </section>

                <section className='grid gap-4 md:grid-cols-2'>
                    <div className='rounded-[1.75rem] border border-border bg-card p-5'>
                        <h2 className='font-heading text-xl'>{t({ hi: 'छह कारक, दो सम्बन्ध', en: 'Six kārakas and two relations' })}</h2>
                        <p className='mt-1 text-sm text-muted-foreground'>
                            {t({
                                hi: 'पाणिनि के अनुसार कारक छह हैं: कर्ता, कर्म, करण, सम्प्रदान, अपादान, अधिकरण। सम्बन्ध (षष्ठी) और सम्बोधन क्रिया से सीधे नहीं जुड़ते, इसलिए कारक नहीं हैं।',
                                en: 'Pāṇini counts six kārakas: agent, object, instrument, recipient, source and location. Relation (sixth case) and address do not attach to the action directly, so they are not kārakas.'
                            })}
                        </p>
                        <ul className='mt-3 space-y-1.5'>
                            {(['karta', 'karma', 'karana', 'sampradana', 'apadana', 'adhikarana'] as KarakaRole[]).map(r => (
                                <li key={r} className='flex items-center gap-2 text-sm'>
                                    <span className={cn('size-2.5 rounded-full', ROLE_DOT[r])} aria-hidden='true' />
                                    <span lang='sa' className='deva w-24 font-semibold'>{KARAKA_ROLES[r].sa}</span>
                                    <span className='text-muted-foreground'>{t(KARAKA_ROLES[r].question)}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className='rounded-[1.75rem] border border-border bg-card p-5'>
                        <h2 className='font-heading text-xl'>{t({ hi: 'कारक और विभक्ति एक नहीं', en: 'Kāraka is not the same as case' })}</h2>
                        <p className='mt-1 text-sm text-muted-foreground'>
                            {t({
                                hi: 'कारक अर्थ है (भूमिका), विभक्ति रूप है (अन्त)। सामान्य वाक्य में कर्ता प्रथमा में आता है, पर कर्मवाच्य में वही कर्ता तृतीया में आता है:',
                                en: 'A kāraka is a meaning (a role); a case is a form (an ending). In an ordinary sentence the agent is in the first case, but in the passive the same agent appears in the third case:'
                            })}
                        </p>
                        <div className='mt-3 space-y-2 rounded-2xl bg-muted/60 p-4'>
                            <p><span lang='sa' className='deva text-lg font-semibold'>रामः पुस्तकं पठति।</span> <span className='text-sm text-muted-foreground'>{t({ hi: '(कर्तृवाच्य)', en: '(active)' })}</span></p>
                            <p><span lang='sa' className='deva text-lg font-semibold'>रामेण पुस्तकं पठ्यते।</span> <span className='text-sm text-muted-foreground'>{t({ hi: '(कर्मवाच्य: राम द्वारा पुस्तक पढ़ी जाती है)', en: '(passive: the book is read by Rāma)' })}</span></p>
                        </div>
                        <p className='mt-2 text-xs text-muted-foreground'>{t({ hi: 'कर्मवाच्य का पूरा पाठ आगे के स्तर में आएगा।', en: 'The passive gets its own lessons at a later level.' })}</p>
                    </div>
                </section>

                <Link href='/sanskrit/grammar/vakya' className='flex items-center justify-between rounded-3xl bg-ink p-5 text-primary-foreground'>
                    <span>
                        <span lang='sa' className='deva block text-xl font-semibold'>वाक्यविश्लेषणम्</span>
                        <span className='text-sm text-primary-foreground/75'>{t({ hi: 'अपना वाक्य लिखकर उसका विश्लेषण देखिए', en: 'Type your own sentence and see its analysis' })}</span>
                    </span>
                    <ArrowRight className='size-5' />
                </Link>
            </div>
            </SanskritGate>
        </Layout>
    )
}

export default KarakaPage
